import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getCauseCodes,
  createCauseCode,
  updateCauseCode,
  deleteCauseCode,
  checkCauseCodeDuplicate,
} from '../api/cause-codeAPI';
import { fetchData } from '../utils/apiHelpers';

const CauseCodeListLayer = () => {
  const userProfile = useUserProfile();
  const [causeCodes, setCauseCodes] = useState([]);
  const [newCauseCode, setNewCauseCode] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchCauseCodes = async () => {
    await fetchData(getCauseCodes, {}, setCauseCodes);
  };


  const validateForm = async () => {
    const newErrors = {};
    if (!newCauseCode.code?.trim()) newErrors.code = 'Code is required';
    if (!newCauseCode.name?.trim()) newErrors.name = 'Name is required';
    if (Object.keys(newErrors).length === 0) {
      try {
        const dup = await checkCauseCodeDuplicate({
          code: newCauseCode.code,
          name: newCauseCode.name,
          excludeId: editingId,
        });
        if (dup.codeExists) newErrors.code = 'Code already exists';
        if (dup.nameExists) newErrors.name = 'Name already exists';
      } catch (err) {
        console.error('Error checking cause code duplicates:', err);
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchCauseCodes();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (causeCodes.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [causeCodes]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCauseCode((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setNewCauseCode(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this cause code?')) return;
    try {
      await deleteCauseCode(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchCauseCodes();
    } catch (err) {
      console.error('Error deleting cause code:', err);
    }
  };

  const handleSave = async () => {
    if (!(await validateForm())) return;
    try {
      const payload = {};
      for (const key in newCauseCode) {
        if (newCauseCode[key] !== null && typeof newCauseCode[key] !== 'object') {
          payload[key] = newCauseCode[key];
        }
      }

      if (editingId) {
        await updateCauseCode(editingId, payload);
      } else {
        await createCauseCode(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      setNewCauseCode({});
      setErrors({});
      await fetchCauseCodes();
    } catch (err) {
      console.error('Error saving cause code:', err);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('cause-codes:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewCauseCode({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Cause Code
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {causeCodes.map((item) => (
                <tr key={item.id}>
                  <td>{item.code}</td>
                  <td>{item.name}</td>
                  <td>{item.description}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('cause-codes:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('cause-codes:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Cause Code' : 'New Cause Code'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Code *</label>
                    <input
                      className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                      name="code"
                      value={newCauseCode.code || ''}
                      onChange={handleInputChange}
                    />
                    {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Name *</label>
                    <input
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={newCauseCode.name || ''}
                      onChange={handleInputChange}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Description</label>
                    <input
                      className="form-control"
                      name="description"
                      value={newCauseCode.description || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CauseCodeListLayer;
